import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SessionShell, SessionHeader, Button, EndSessionButton, NextButton, NotesDrawer } from '../../ui';
import { useNavigationSync } from '../../context/navigationContext';
import { usePostMessage } from '../../context/messageContext';
import { useSessionState } from '../../context/sessionStateContext';
import { LobbyView } from './LobbyView';
import { ScreenSizeGuard } from './ScreenSizeGuard';
import { usePatientNotes } from './PatientNotes';
import { useSessionPeople, useConfirmEndSession, useConfirmExitSession, buildMenuItems, CLINICIAN_STEPS } from './hooks';

// Records "joined" once per page load and appointment. Module-level (not a ref): the patient lobby
// mounts twice while the responsive routes settle, which used to record two "joined" events.
const joinedSent = new Set();
const useJoinedEvent = (appointmentId) => {
  const { recordEvent } = useSessionState();
  useEffect(() => {
    if (!appointmentId || joinedSent.has(appointmentId)) return;
    joinedSent.add(appointmentId);
    recordEvent('joined');
  }, [appointmentId, recordEvent]);
};

// A clinician who re-joins mid-session (page reload, or the reload the legacy 'welcomeScreen'
// handshake forces when the patient reloads) gets "Resume" instead of "Begin": it goes back to the
// step stored in the shared session state without re-patching it, so test_key survives and the
// Examinations picker reopens the running test (test-selection.js). Not automatic: navigating
// away before the patient's own welcomeScreen handshake arrives would bounce the clinician back.
const RESUMABLE_STEPS = CLINICIAN_STEPS.map((s) => s.id);
export const resumableStep = (state) => (state?.started_at && !state?.ended_at && RESUMABLE_STEPS.includes(state?.step) ? state.step : null);

/** C1 - clinician lobby ("IBISengage Appointment"): tiles, End Session, Add Notes, Begin. */
export const ClinicianLobby = () => {
  const navigate = useNavigate();
  const people = useSessionPeople();
  const { setActiveClinicianTab } = useNavigationSync() || {};
  const postMessage = usePostMessage();
  const { state, status, patch, recordEvent } = useSessionState();
  const endSession = useConfirmEndSession();
  const [notesOpen, setNotesOpen] = useState(false);
  useJoinedEvent(people.appointmentId);

  const open = useCallback(
    (tab) => {
      navigate(`${process.env.PUBLIC_URL}/clinician`);
      setActiveClinicianTab?.(tab);
      if (tab === 'test') postMessage({ type: 'testNavigationReset' });
    },
    [navigate, setActiveClinicianTab, postMessage],
  );
  const resumeStep = status === 'ready' ? resumableStep(state) : null;

  const goTo = useCallback(
    (tab) => {
      open(tab);
      patch({ step: tab, sub_step: null }).catch(() => {});
    },
    [open, patch],
  );

  const begin = () => {
    if (resumeStep) return open(resumeStep);
    recordEvent('started');
    goTo('calibration-selection');
  };

  return (
    <SessionShell
      fixed
      header={
        <SessionHeader
          title='IBISengage Appointment'
          showLogo
          onSeeNotes={() => setNotesOpen(true)}
          menuItems={buildMenuItems({ activeTab: null, onNavigate: goTo, onEnd: endSession })}
        />
      }
    >
      <LobbyView
        people={people}
        localFirst
        left={<EndSessionButton onClick={endSession} />}
        right={
          <>
            <Button variant='secondary' size='xl' onClick={() => setNotesOpen(true)}>
              Add Notes
            </Button>
            <NextButton onClick={begin} data-testid='lobby-begin'>
              {resumeStep ? 'Resume' : 'Begin'}
            </NextButton>
          </>
        }
      />
      <NotesDrawer open={notesOpen} onClose={() => setNotesOpen(false)} appointmentId={people.appointmentId} currentUserId={people.currentUserId} patientName={people.patientName} />
    </SessionShell>
  );
};

/** P1 - patient lobby: clinician tile on top, own tile + controls, Exit Session. */
export const PatientLobby = ({ title = 'IBISengage Appointment', note }) => {
  const people = useSessionPeople();
  const exitSession = useConfirmExitSession();
  const notes = usePatientNotes();
  useJoinedEvent(people.appointmentId);
  return (
    <ScreenSizeGuard>
      <SessionShell fixed header={<SessionHeader title={title} showLogo onSeeNotes={notes.onSeeNotes} />}>
        <LobbyView people={people} localFirst={false} note={note} left={<EndSessionButton onClick={exitSession}>Exit Session</EndSessionButton>} />
      </SessionShell>
      {notes.drawer}
    </ScreenSizeGuard>
  );
};

/** Role switch used by the /start/<room> routes. */
export const SessionLobby = () => {
  const { isPatient } = useSessionPeople();
  return isPatient ? <PatientLobby /> : <ClinicianLobby />;
};

export default SessionLobby;
